import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  deleteField,
  onSnapshot,
  serverTimestamp,
  Timestamp,
  DocumentReference,
  CollectionReference,
  DocumentData,
  arrayUnion,
  arrayRemove,
} from 'firebase/firestore';
import { ref as storageRef, deleteObject } from 'firebase/storage';
import { userStorageQuotaService } from './userStorageQuotaService';
import { db, storage } from '../firebase/config';
import {
  ChatQuiz,
  ChatQuizSet,
  ClassChatMetaDoc,
  StudentQuizResponse,
  QuizType,
  ChoiceOption,
  ConnectedStudentInfo,
} from '../types/chatQuiz';

const MAX_MULTI_RESPONSES = 5;

// Firebase Storage URL에서 경로 추출
function extractStoragePathFromUrl(url: string): string | null {
  try {
    // Firebase Storage URL 형식: https://firebasestorage.googleapis.com/v0/b/{bucket}/o/{encodedPath}?alt=media&token={token}
    const match = url.match(/\/o\/(.+?)\?/);
    if (match && match[1]) {
      // URL 디코딩
      return decodeURIComponent(match[1]);
    }
    return null;
  } catch (e) {
    console.error('❌ URL 파싱 실패:', e);
    return null;
  }
}

interface ClassChatRefs {
  userId: string;
  classId: string;
  metaRef: DocumentReference<DocumentData>;
  quizzesCollectionRef: CollectionReference<DocumentData>;
}

// 클래스 코드별로 참조를 캐싱하여 중복 조회를 줄인다.
const classChatRefsCache: Map<string, ClassChatRefs> = new Map();
// HMR/순환 참조 시점을 방어하기 위해 classCodeService를 동적 로드
let classCodeServiceModulePromise:
  | Promise<typeof import('./classCodeService')>
  | null = null;

const loadClassCodeService = async () => {
  if (!classCodeServiceModulePromise) {
    classCodeServiceModulePromise = import('./classCodeService');
  }
  const mod = await classCodeServiceModulePromise;
  return mod.classCodeService;
};

interface QuizSetDoc {
  quizzes: {
    [quizId: string]: any;
  };
  lastUpdated?: Timestamp;
  title?: string;
}

export class ChatQuizService {
  private resolveMemberGroup(userId: string, classId: string, memberGroup?: string): string {
    let resolved = memberGroup;
    if (typeof resolved === 'undefined') {
      try {
        if (typeof window !== 'undefined' && window.localStorage) {
          const key = `selected_member_group_${userId}_${classId}`;
          const saved = window.localStorage.getItem(key);
          if (saved) {
            resolved = saved;
          }
        }
      } catch {
        // ignore
      }
    }
    return (resolved || 'A').toString().trim().toUpperCase();
  }

  private getQuizGroupData(
    quizData: any,
    memberGroup: string
  ): {
    responses: { [studentId: string]: StudentQuizResponse | StudentQuizResponse[] };
    answeredStudents: string[];
  } {
    const group = (memberGroup || 'A').toString().trim().toUpperCase();
    const responses =
      (quizData?.responsesByGroup && quizData.responsesByGroup[group]) ||
      (group === 'A' ? quizData?.responses : undefined) ||
      {};
    const answeredStudentsRaw =
      (quizData?.answeredStudentsByGroup && quizData.answeredStudentsByGroup[group]) ||
      (group === 'A' ? quizData?.answeredStudents : undefined) ||
      [];
    return {
      responses: responses || {},
      answeredStudents: Array.isArray(answeredStudentsRaw) ? answeredStudentsRaw : [],
    };
  }
  private async getClassChatRefs(classCode: string): Promise<ClassChatRefs> {
    console.log('🔍 getClassChatRefs 시작:', { classCode });

    const cachedRefs = classChatRefsCache.get(classCode);
    if (cachedRefs) {
      console.log('✅ 캐시된 refs 반환:', { metaPath: cachedRefs.metaRef.path });
      return cachedRefs;
    }

    console.log('🔄 캐시 없음, classCodeService 호출');
    const codeService = await loadClassCodeService();
    console.log('✅ classCodeService 로드 성공');

    const classLocation = await codeService.getClassLocationByCode(classCode);
    console.log('🔍 getClassLocationByCode 결과:', { classLocation });

    if (!classLocation) {
      console.error('❌ 클래스 코드를 찾을 수 없음:', { classCode });
      throw new Error('해당 클래스 코드를 찾을 수 없습니다.');
    }

    const { userId, classId } = classLocation;
    console.log('✅ 클래스 정보 추출:', { userId, classId });

    const metaRef = doc(db, 'users', userId, 'classes', classId, 'ClassChat', 'meta');
    const quizzesCollectionRef = collection(db, 'users', userId, 'classes', classId, 'ClassChatQuizzes');

    const result = { userId, classId, metaRef, quizzesCollectionRef };
    classChatRefsCache.set(classCode, result);
    return result;
  }

  private async ensureMetaDoc(
    classCode: string,
    metaRef: DocumentReference<DocumentData>,
    quizzesCollectionRef: CollectionReference<DocumentData>
  ): Promise<ClassChatMetaDoc> {
    console.log('🔍 ensureMetaDoc 시작:', { classCode, metaPath: metaRef.path });

    const snapshot = await getDoc(metaRef);
    console.log('🔍 meta 문서 조회 결과:', { exists: snapshot.exists() });

    if (snapshot.exists()) {
      const data = snapshot.data() as ClassChatMetaDoc;
      console.log('✅ 기존 meta 문서 존재, 데이터 반환');
      return {
        quizOrder: Array.isArray(data.quizOrder) ? data.quizOrder : [],
        activeQuizId: data.activeQuizId ?? null,
        activeMemberGroup: (data as any).activeMemberGroup ?? null,
        activeQuizSetId: data.activeQuizSetId ?? null,
        activeQuizContainerId: (data as any).activeQuizContainerId ?? null,
        studentCreateMode: (data as any).studentCreateMode ?? false,
        studentCreateSetId: (data as any).studentCreateSetId ?? null,
        isMultiAnswerMode: data.isMultiAnswerMode ?? false,
        isPublicMode: data.isPublicMode ?? false,
        isChatOpen: data.isChatOpen ?? false,
        connectedStudents: data.connectedStudents ?? {},
        connectedStudentsByGroup: (data as any).connectedStudentsByGroup ?? {},
        lastUpdated: data.lastUpdated instanceof Timestamp ? data.lastUpdated : Timestamp.now(),
      };
    }

    console.log('📝 meta 문서가 존재하지 않아 새로 생성');

    const defaultMeta: ClassChatMetaDoc = {
      quizOrder: [],
      activeQuizId: null,
      activeMemberGroup: null,
      activeQuizSetId: null,
      activeQuizContainerId: null,
      studentCreateMode: false,
      studentCreateSetId: null,
      isMultiAnswerMode: false,
      isPublicMode: false,
      isChatOpen: false,
      connectedStudents: {},
      connectedStudentsByGroup: {},
      lastUpdated: Timestamp.now(),
    };

    console.log('📤 setDoc 호출 시도:', { metaPath: metaRef.path });
    try {
      await setDoc(metaRef, defaultMeta);
      console.log('✅ setDoc 성공, 새로운 meta 문서 생성됨');
      return defaultMeta;
    } catch (setDocError: any) {
      console.error('❌ setDoc 실패:', {
        error: setDocError,
        errorCode: setDocError?.code,
        errorMessage: setDocError?.message,
        metaPath: metaRef.path
      });
      throw setDocError;
    }
  }


  async listQuizSets(classCode: string): Promise<ChatQuizSet[]> {
    const { userId, classId } = await this.getClassChatRefs(classCode);
    const collectionRef = collection(db, 'users', userId, 'classes', classId, 'ClassChatQuizSets');
    const snap = await getDocs(collectionRef);
    return snap.docs
      .filter((d) => d.id !== 'main') // main 문서 제외
      .map((d) => {
      const data = d.data() as QuizSetDoc;
      return {
        id: d.id,
        title: (data as any).title || '무제 세트',
        quizzes: data.quizzes || {},
        createdAt: (data as any).createdAt,
        updatedAt: data.lastUpdated,
      };
    });
  }

  async getQuizSetById(classCode: string, setId: string): Promise<ChatQuizSet | null> {
    const { userId, classId } = await this.getClassChatRefs(classCode);
    const setRef = doc(db, 'users', userId, 'classes', classId, 'ClassChatQuizSets', setId);
    const snap = await getDoc(setRef);
    if (!snap.exists()) return null;
    const data = snap.data() as QuizSetDoc;
    return {
      id: snap.id,
      title: (data as any).title || '무제 세트',
      quizzes: data.quizzes || {},
      createdAt: (data as any).createdAt,
      updatedAt: data.lastUpdated,
    };
  }

  /**
   * 문제 만들기 등에서 세트 문서 실시간 구독. 학생 제출 시 코디 화면에서 즉시 반영할 때 사용.
   */
  listenToQuizSet(
    classCode: string,
    setId: string,
    callback: (set: ChatQuizSet | null) => void
  ): () => void {
    let unsub: (() => void) | null = null;
    let cancelled = false;
    this.getClassChatRefs(classCode).then(({ userId, classId }) => {
      if (cancelled) return;
      const setRef = doc(db, 'users', userId, 'classes', classId, 'ClassChatQuizSets', setId);
      unsub = onSnapshot(
        setRef,
        (snap) => {
          if (!snap.exists()) {
            callback(null);
            return;
          }
          const data = snap.data() as QuizSetDoc;
          callback({
            id: snap.id,
            title: (data as any).title || '무제 세트',
            quizzes: data.quizzes || {},
            createdAt: (data as any).createdAt,
            updatedAt: data.lastUpdated,
          });
        },
        (err) => {
          console.error('listenToQuizSet error', err);
          callback(null);
        }
      );
    });
    return () => {
      cancelled = true;
      if (unsub) unsub();
    };
  }

  async createQuizSet(
    classCode: string,
    title: string,
    quizzes: Array<Omit<ChatQuiz, 'id' | 'createdAt' | 'updatedAt'>>
  ): Promise<string> {
    const { userId, classId } = await this.getClassChatRefs(classCode);
    const collectionRef = collection(db, 'users', userId, 'classes', classId, 'ClassChatQuizSets');
    const setId = `set_${Date.now()}`;
    const setRef = doc(collectionRef, setId);

    const now = Timestamp.now();
    const quizzesMap: Record<string, any> = {};
    quizzes.forEach((q, idx) => {
      const quizId = (q as any).id || `quiz_${Date.now()}_${idx}`;
      const quizData: any = {
        ...q,
        id: quizId,
        order: idx,
        createdAt: now,
        updatedAt: now,
        responses: {},
        answeredStudents: [],
      };
      // undefined 값을 빈 문자열로 변환 (Firestore는 undefined를 허용하지 않음)
      if (quizData.questionImageUrl === undefined) {
        quizData.questionImageUrl = '';
      }
      if (quizData.guide === undefined) {
        quizData.guide = '';
      }
      quizzesMap[quizId] = quizData;
    });

    await setDoc(setRef, {
      title,
      quizzes: quizzesMap,
      createdAt: now,
      lastUpdated: now,
    });

    return setId;
  }

  async updateQuizSet(
    classCode: string,
    setId: string,
    payload: { title?: string; quizzes?: Array<Omit<ChatQuiz, 'createdAt' | 'updatedAt'>> }
  ): Promise<void> {
    const { userId, classId } = await this.getClassChatRefs(classCode);
    const setRef = doc(db, 'users', userId, 'classes', classId, 'ClassChatQuizSets', setId);
    const updates: Record<string, any> = {
      lastUpdated: serverTimestamp(),
    };

    if (payload.title !== undefined) {
      updates.title = payload.title;
    }

    if (payload.quizzes) {
      const now = Timestamp.now();
      const quizzesMap: Record<string, any> = {};
      payload.quizzes.forEach((q, idx) => {
        const quizId = (q as any).id || `quiz_${Date.now()}_${idx}`;
        const quizData: any = {
          ...q,
          id: quizId,
          order: q.order ?? idx,
          createdAt: (q as any).createdAt ?? now,
          updatedAt: now,
          responses: q.responses || {},
          answeredStudents: q.answeredStudents || [],
        };
        // undefined 값을 빈 문자열로 변환 (Firestore는 undefined를 허용하지 않음)
        if (quizData.questionImageUrl === undefined) {
          quizData.questionImageUrl = '';
        }
        if (quizData.guide === undefined) {
          quizData.guide = '';
        }
        quizzesMap[quizId] = quizData;
      });
      updates.quizzes = quizzesMap;
    }

    await updateDoc(setRef, updates);
  }

  async deleteQuizSet(classCode: string, setId: string): Promise<void> {
    const { userId, classId } = await this.getClassChatRefs(classCode);
    const setRef = doc(db, 'users', userId, 'classes', classId, 'ClassChatQuizSets', setId);
    
    // 세트 데이터 가져오기 (이미지 URL 확인용)
    const setSnap = await getDoc(setRef);
    if (setSnap.exists()) {
      const setData = setSnap.data() as QuizSetDoc;
      const quizzes = setData.quizzes || {};
      
      // 세트에 포함된 모든 퀴즈의 이미지 삭제
      for (const [, quizData] of Object.entries(quizzes)) {
        const questionImageUrl = (quizData as any).questionImageUrl;
        if (questionImageUrl && typeof questionImageUrl === 'string') {
          try {
            const path = extractStoragePathFromUrl(questionImageUrl);
            if (path) {
              const fileRef = storageRef(storage, path);
              await deleteObject(fileRef);
              userStorageQuotaService.invalidateUsageCache();
              console.log('✅ 세트 퀴즈 이미지 삭제 완료:', path);
            }
          } catch (error) {
            console.error('❌ 세트 퀴즈 이미지 삭제 실패:', error);
            // 이미지 삭제 실패해도 세트 삭제는 계속 진행
          }
        }
      }
    }
    
    await deleteDoc(setRef);
  }

  private normalizeQuizData(quizId: string, data: DocumentData): ChatQuiz {
    const rawCreatedAt = data.createdAt;
    let createdAt: Timestamp;

    if (rawCreatedAt instanceof Timestamp) {
      createdAt = rawCreatedAt;
    } else if (rawCreatedAt instanceof Date) {
      createdAt = Timestamp.fromDate(rawCreatedAt);
    } else if (rawCreatedAt && typeof rawCreatedAt === 'object' && 'seconds' in rawCreatedAt && 'nanoseconds' in rawCreatedAt) {
      createdAt = new Timestamp(rawCreatedAt.seconds, rawCreatedAt.nanoseconds);
    } else {
      createdAt = Timestamp.now();
    }

    const rawUpdatedAt = data.updatedAt;
    let updatedAt: Timestamp | null = null;

    if (rawUpdatedAt instanceof Timestamp) {
      updatedAt = rawUpdatedAt;
    } else if (rawUpdatedAt instanceof Date) {
      updatedAt = Timestamp.fromDate(rawUpdatedAt);
    } else if (rawUpdatedAt && typeof rawUpdatedAt === 'object' && 'seconds' in rawUpdatedAt && 'nanoseconds' in rawUpdatedAt) {
      updatedAt = new Timestamp(rawUpdatedAt.seconds, rawUpdatedAt.nanoseconds);
    }

    const quiz: ChatQuiz = {
      id: data.id || quizId,
      type: data.type,
      question: data.question,
      questionImageUrl: data.questionImageUrl,
      choices: data.choices,
      correctAnswers: data.correctAnswers,
      answers: data.answers,
      guide: data.guide,
      createdAt,
      updatedAt,
      order: data.order,
      defaultMultiAnswerMode: data.defaultMultiAnswerMode ?? false,
      defaultPublicMode: data.defaultPublicMode ?? false,
      responses: data.responses || {},
      answeredStudents: Array.isArray(data.answeredStudents) ? data.answeredStudents : [],
    };

    return quiz;
  }

  async saveQuiz(
    classCode: string,
    type: QuizType,
    question: string,
    options: {
      questionImageUrl?: string;
      choices?: ChoiceOption[];
      correctAnswers?: number[];
      answers?: string[];
      guide?: string;
      controlSettings?: {
        multiAnswerMode?: boolean;
        publicMode?: boolean;
      };
      setId?: string; // 퀴즈를 저장할 세트 ID (없으면 첫 세트 자동 생성)
    }
  ): Promise<string> {
    const { metaRef, quizzesCollectionRef, userId, classId } = await this.getClassChatRefs(classCode);
    await this.ensureMetaDoc(classCode, metaRef, quizzesCollectionRef);
    
    // setId가 제공되면 해당 세트를 사용하고, 없으면 첫 세트를 자동 생성
    let targetSetId = options.setId;
    if (!targetSetId) {
      // 세트가 없으면 첫 세트를 자동 생성
      const allSets = await this.listQuizSets(classCode);
      if (allSets.length === 0) {
        // 첫 세트 생성
        targetSetId = `set_${Date.now()}`;
        const firstSetRef = doc(db, 'users', userId, 'classes', classId, 'ClassChatQuizSets', targetSetId);
        await setDoc(firstSetRef, {
          title: '기본 세트',
          quizzes: {},
          createdAt: Timestamp.now(),
          lastUpdated: Timestamp.now(),
        });
        // 메타에 첫 세트 ID 저장
        await updateDoc(metaRef, {
          activeQuizSetId: targetSetId,
          activeQuizContainerId: targetSetId,
          lastUpdated: serverTimestamp(),
        });
      } else {
        // 기존 세트가 있으면 첫 번째 세트 사용
        targetSetId = allSets[0].id;
      }
    }
    
    const targetSetRef = doc(db, 'users', userId, 'classes', classId, 'ClassChatQuizSets', targetSetId);
    
    // 세트 문서가 존재하는지 확인 (없으면 에러)
    const setSnap = await getDoc(targetSetRef);
    if (!setSnap.exists()) {
      throw new Error(`퀴즈 세트를 찾을 수 없습니다: ${targetSetId}`);
    }
    const quizSet = setSnap.data() as QuizSetDoc;

    const newQuizId = `quiz_${Date.now()}`;
    const createdAt = Timestamp.now();

    // 기존 퀴즈들의 최대 order 값을 찾아서 +1 (마지막에 추가)
    const existingQuizzes = quizSet.quizzes || {};
    let maxOrder = -1;
    Object.values(existingQuizzes).forEach((quiz: any) => {
      const order = quiz.order ?? -1;
      if (order > maxOrder) {
        maxOrder = order;
      }
    });
    const newOrder = maxOrder + 1;

    const quizData: Record<string, any> = {
      id: newQuizId,
      type,
      question,
      createdAt,
      updatedAt: createdAt,
      order: newOrder,
      responses: {},
      answeredStudents: [],
    };

    // questionImageUrl이 undefined가 아닐 때만 추가 (Firestore는 undefined를 허용하지 않음)
    if (options.questionImageUrl !== undefined) {
      quizData.questionImageUrl = options.questionImageUrl || null;
    }

    if (type === 'choice') {
      if (options.choices) {
        quizData.choices = options.choices;
      }
      if (options.correctAnswers) {
        quizData.correctAnswers = options.correctAnswers;
      }
    } else if (type === 'short') {
      if (options.answers) {
        quizData.answers = options.answers;
      }
    } else if (type === 'essay') {
      if (options.guide) {
        quizData.guide = options.guide;
      }
    }

    quizData.defaultMultiAnswerMode =
      options.controlSettings?.multiAnswerMode ?? false;
    quizData.defaultPublicMode = options.controlSettings?.publicMode ?? false;

    await updateDoc(
      targetSetRef,
      {
        [`quizzes.${newQuizId}`]: quizData,
        lastUpdated: serverTimestamp(),
      }
    );

    // meta의 quizOrder는 더 이상 사용하지 않음 (각 세트가 자체 order 관리)

    return newQuizId;
  }

  async updateQuiz(
    classCode: string,
    quizId: string,
    type: QuizType,
    question: string,
    options: {
      questionImageUrl?: string;
      choices?: ChoiceOption[];
      correctAnswers?: number[];
      answers?: string[];
      guide?: string;
      controlSettings?: {
        multiAnswerMode?: boolean;
        publicMode?: boolean;
      };
      setId?: string; // 퀴즈가 속한 세트 ID (필수)
    }
  ): Promise<void> {
    const { userId, classId } = await this.getClassChatRefs(classCode);
    
    // setId가 필수 (없으면 에러)
    if (!options.setId) {
      throw new Error('퀴즈를 수정하려면 세트 ID가 필요합니다.');
    }
    const targetSetId = options.setId;
    const targetSetRef = doc(db, 'users', userId, 'classes', classId, 'ClassChatQuizSets', targetSetId);
    
    // 세트 문서가 존재하는지 확인 (없으면 에러)
    const setSnap = await getDoc(targetSetRef);
    if (!setSnap.exists()) {
      throw new Error(`퀴즈 세트를 찾을 수 없습니다: ${targetSetId}`);
    }
    const quizSet = setSnap.data() as QuizSetDoc;
    const existingQuiz = quizSet.quizzes?.[quizId];

    if (!existingQuiz) {
      throw new Error('퀴즈 문서를 찾을 수 없습니다.');
    }

    const basePath = `quizzes.${quizId}`;
    const updates: Record<string, any> = {};

    if (type === 'choice') {
      if (options.choices !== undefined) {
        updates[`${basePath}.choices`] = options.choices;
      }
      if (options.correctAnswers !== undefined) {
        updates[`${basePath}.correctAnswers`] = options.correctAnswers;
      }
      updates[`${basePath}.answers`] = deleteField();
      updates[`${basePath}.guide`] = deleteField();
    } else if (type === 'short') {
      if (options.answers !== undefined) {
        updates[`${basePath}.answers`] = options.answers;
      }
      updates[`${basePath}.choices`] = deleteField();
      updates[`${basePath}.correctAnswers`] = deleteField();
      updates[`${basePath}.guide`] = deleteField();
    } else if (type === 'essay') {
      if (options.guide !== undefined) {
        updates[`${basePath}.guide`] = options.guide;
      }
      updates[`${basePath}.choices`] = deleteField();
      updates[`${basePath}.correctAnswers`] = deleteField();
      updates[`${basePath}.answers`] = deleteField();
    }

    updates[`${basePath}.type`] = type;
    updates[`${basePath}.question`] = question;
    updates[`${basePath}.questionImageUrl`] = options.questionImageUrl ?? null;
    updates[`${basePath}.updatedAt`] = serverTimestamp();
    updates[`${basePath}.defaultMultiAnswerMode`] = options.controlSettings?.multiAnswerMode ?? false;
    updates[`${basePath}.defaultPublicMode`] = options.controlSettings?.publicMode ?? false;

    await updateDoc(targetSetRef, updates);
  }

  async openChat(classCode: string, memberGroup?: string): Promise<void> {
    const { metaRef, quizzesCollectionRef, userId, classId } = await this.getClassChatRefs(classCode);
    await this.ensureMetaDoc(classCode, metaRef, quizzesCollectionRef);
    const resolvedGroup = this.resolveMemberGroup(userId, classId, memberGroup);

    // connectedStudents는 유지하고 isChatOpen만 업데이트 (그룹 스코프 포함)
    await updateDoc(metaRef, {
      isChatOpen: true,
      activeMemberGroup: resolvedGroup,
      lastUpdated: serverTimestamp(),
    });
  }

  async closeChat(classCode: string): Promise<void> {
    const { metaRef } = await this.getClassChatRefs(classCode);
    // 챗 종료 시 접속/재생 상태를 초기화 (그룹 스코프도 해제)
    await updateDoc(metaRef, {
      isChatOpen: false,
      activeMemberGroup: null,
      connectedStudents: {},
      connectedStudentsByGroup: {},
      activeQuizId: null,
      activeQuizSetId: null,
      activeQuizContainerId: null,
      lastUpdated: serverTimestamp(),
    });
  }

  // meta 문서 존재 여부 확인 및 생성 (중앙 집중식)
  async ensureMetaExists(classCode: string): Promise<void> {
    console.log('🔧 ensureMetaExists 시작:', { classCode });

    try {
      const { metaRef, quizzesCollectionRef } = await this.getClassChatRefs(classCode);
      console.log('✅ getClassChatRefs 성공:', {
        metaRef: metaRef.path,
        quizzesCollectionRef: quizzesCollectionRef.path
      });

      await this.ensureMetaDoc(classCode, metaRef, quizzesCollectionRef);
      console.log('✅ meta 문서 존재 확인/생성 완료');
    } catch (error: any) {
      console.error('❌ ensureMetaExists 실패:', {
        error: error,
        errorCode: error?.code,
        errorMessage: error?.message,
        classCode
      });
      throw error;
    }
  }

  // meta 문서 초기화를 위한 public 메소드 (테스트용)
  async initializeChatMeta(classCode: string): Promise<void> {
    return this.ensureMetaExists(classCode);
  }

  async addConnectedStudentToChat(
    classCode: string,
    studentId: string,
    info: ConnectedStudentInfo,
    memberGroup?: string
  ): Promise<void> {
    try {
      console.log('🔍 addConnectedStudentToChat 시작:', { classCode, studentId, info });
      
      const { metaRef, userId, classId } = await this.getClassChatRefs(classCode);
      console.log('✅ getClassChatRefs 성공:', { metaRef: metaRef.path });
      const resolvedGroup = this.resolveMemberGroup(userId, classId, memberGroup);
      
      // meta 문서가 존재하는 경우에만 접속 정보 기록 (코디가 채팅 시작 시 meta 문서가 생성됨)
      let metaSnap;
      try {
        metaSnap = await getDoc(metaRef);
        console.log('✅ meta 문서 조회 성공:', { exists: metaSnap.exists() });
      } catch (readError: any) {
        console.error('❌ meta 문서 조회 실패:', readError, {
          errorCode: readError?.code,
          errorMessage: readError?.message,
        });
        // meta 문서 조회 실패 시 (권한 없음 등) 조용히 무시하고 진행
        console.log('⚠️ meta 문서 조회 실패로 접속 정보 기록 생략:', { classCode, studentId });
        return;
      }
      
      // meta 문서가 존재하지 않으면 접속 정보 기록 생략 (코디가 채팅 시작 시 생성됨)
      if (!metaSnap.exists()) {
        console.log('⚠️ meta 문서가 존재하지 않아 접속 정보 기록 생략:', { classCode, studentId });
        return;
      }
      
      // meta 문서가 존재하면 일반 업데이트
      try {
        const metaData = metaSnap.data() as ClassChatMetaDoc;
        const activeGroup = ((metaData as any).activeMemberGroup ?? 'A').toString().trim().toUpperCase();
        // 다른 그룹이 재생/오픈 중이면, 현재 그룹은 접속 기록을 남기지 않는다 (상호작용 폭증 방지)
        if (activeGroup && activeGroup !== resolvedGroup) {
          console.log('ℹ️ 다른 그룹이 활성 상태라 접속 기록 생략:', { activeGroup, resolvedGroup, studentId });
          return;
        }
        await updateDoc(metaRef, {
          [`connectedStudentsByGroup.${resolvedGroup}.${studentId}`]: {
            ...info,
            connectedAt: serverTimestamp(),
          },
          ...(resolvedGroup === 'A'
            ? {
                [`connectedStudents.${studentId}`]: {
                  ...info,
                  connectedAt: serverTimestamp(),
                },
              }
            : {}),
          lastUpdated: serverTimestamp(),
        });
        console.log('✅ 멤버 접속 정보 업데이트 완료:', { classCode, studentId });
      } catch (updateError: any) {
        console.error('❌ 멤버 접속 정보 업데이트 실패:', updateError, {
          classCode,
          studentId,
          errorCode: updateError?.code,
          errorMessage: updateError?.message,
        });
        throw updateError;
      }
    } catch (error: any) {
      console.error('❌ addConnectedStudentToChat 전체 실패:', error, {
        classCode,
        studentId,
        errorCode: error?.code,
        errorMessage: error?.message,
        errorStack: error?.stack,
      });
      throw error;
    }
  }

  async removeConnectedStudentFromChat(
    classCode: string,
    studentId: string,
    memberGroup?: string
  ): Promise<void> {
    const { metaRef, quizzesCollectionRef, userId, classId } = await this.getClassChatRefs(classCode);
    await this.ensureMetaDoc(classCode, metaRef, quizzesCollectionRef);
    const resolvedGroup = this.resolveMemberGroup(userId, classId, memberGroup);
    await updateDoc(metaRef, {
      [`connectedStudentsByGroup.${resolvedGroup}.${studentId}`]: deleteField(),
      ...(resolvedGroup === 'A' ? { [`connectedStudents.${studentId}`]: deleteField() } : {}),
      lastUpdated: serverTimestamp(),
    });
  }

  async getQuizzes(classCode: string): Promise<ChatQuiz[]> {
    // getQuizzes는 더 이상 사용하지 않음 (각 세트별로 퀴즈를 관리)
    // 호환성을 위해 빈 배열 반환
    return [];
  }

  async getQuizById(classCode: string, quizId: string): Promise<ChatQuiz | null> {
    // 모든 세트를 검색하여 퀴즈 찾기
    const { userId, classId } = await this.getClassChatRefs(classCode);
    const collectionRef = collection(db, 'users', userId, 'classes', classId, 'ClassChatQuizSets');
    const snap = await getDocs(collectionRef);
    
    for (const docSnap of snap.docs) {
      if (docSnap.id === 'main') continue; // main 문서 제외
      const setData = docSnap.data() as QuizSetDoc;
      const quizData = setData.quizzes?.[quizId];
      if (quizData) {
        return this.normalizeQuizData(quizId, quizData);
      }
    }
    
    return null;
  }

  async deleteQuiz(classCode: string, quizId: string, setId?: string): Promise<void> {
    const { userId, classId } = await this.getClassChatRefs(classCode);
    
    // setId가 필수 (없으면 에러)
    if (!setId) {
      throw new Error('퀴즈를 삭제하려면 세트 ID가 필요합니다.');
  }
    const targetSetId = setId;
    const targetSetRef = doc(db, 'users', userId, 'classes', classId, 'ClassChatQuizSets', targetSetId);
    
    // 세트 문서가 존재하는지 확인 (없으면 에러)
    const setSnap = await getDoc(targetSetRef);
    if (!setSnap.exists()) {
      throw new Error(`퀴즈 세트를 찾을 수 없습니다: ${targetSetId}`);
    }
    const quizSet = setSnap.data() as QuizSetDoc;

    // 퀴즈 데이터 가져오기 (이미지 URL 확인용)
    const quizData = quizSet.quizzes?.[quizId];
    if (quizData) {
      // 이미지가 있으면 Firebase Storage에서 삭제
      const questionImageUrl = quizData.questionImageUrl;
      if (questionImageUrl && typeof questionImageUrl === 'string') {
        try {
          const path = extractStoragePathFromUrl(questionImageUrl);
          if (path) {
            const fileRef = storageRef(storage, path);
            await deleteObject(fileRef);
            userStorageQuotaService.invalidateUsageCache();
            console.log('✅ 퀴즈 이미지 삭제 완료:', path);
          }
        } catch (error) {
          console.error('❌ 퀴즈 이미지 삭제 실패:', error);
          // 이미지 삭제 실패해도 퀴즈 삭제는 계속 진행
        }
      }
    }

    await updateDoc(targetSetRef, {
      [`quizzes.${quizId}`]: deleteField(),
      lastUpdated: serverTimestamp(),
    });

    // activeQuizId가 삭제된 퀴즈와 같으면 null로 설정
    const { metaRef } = await this.getClassChatRefs(classCode);
    const metaSnap = await getDoc(metaRef);
    if (metaSnap.exists()) {
      const meta = metaSnap.data() as ClassChatMetaDoc;
    if (meta.activeQuizId === quizId) {
        await updateDoc(metaRef, {
          activeQuizId: null,
          lastUpdated: serverTimestamp(),
        });
    }
    }
  }

  async playQuiz(
    classCode: string,
    quizId: string,
    isMultiAnswerMode?: boolean,
    isPublicMode?: boolean,
    setId?: string,
    memberGroup?: string
  ): Promise<void> {
    const { metaRef, userId, classId } = await this.getClassChatRefs(classCode);
    const resolvedGroup = this.resolveMemberGroup(userId, classId, memberGroup);

    // 전달된 setId로만 1회 읽기 (전체 세트 스캔 없음)
    if (!setId) {
      throw new Error('퀴즈를 재생하려면 세트 ID가 필요합니다.');
    }

    const targetSetRef = doc(db, 'users', userId, 'classes', classId, 'ClassChatQuizSets', setId);
    const setSnap = await getDoc(targetSetRef);
    if (!setSnap.exists()) {
      throw new Error(`퀴즈 세트를 찾을 수 없습니다: ${setId}`);
    }
    const quizSet = setSnap.data() as QuizSetDoc;
    const quizData = quizSet.quizzes?.[quizId];

    if (!quizData) {
      throw new Error('퀴즈 문서를 찾을 수 없습니다.');
    }

    const metaUpdates: Record<string, any> = {
      activeQuizId: quizId,
      // 단일 퀴즈 재생은 항상 세트 "재생" 상태를 만들지 않는다.
      // (세트 재생은 playQuizSet에서만 activeQuizSetId를 세팅)
      activeQuizSetId: null,
      // 하지만 퀴즈 데이터가 저장된 컨테이너(세트 문서) 위치는 별도로 기록한다.
      activeQuizContainerId: setId,
      isChatOpen: true, // 재생 시 챗을 자동으로 연다
      activeMemberGroup: resolvedGroup,
      lastUpdated: serverTimestamp(),
    };

    // isMultiAnswerMode와 isPublicMode는 항상 명시적으로 설정 (undefined가 아닐 때)
    if (isMultiAnswerMode !== undefined) {
      metaUpdates.isMultiAnswerMode = isMultiAnswerMode;
      console.log('✅ playQuiz: isMultiAnswerMode 설정:', isMultiAnswerMode);
    } else {
      console.warn('⚠️ playQuiz: isMultiAnswerMode가 undefined입니다');
    }
    if (isPublicMode !== undefined) {
      metaUpdates.isPublicMode = isPublicMode;
      console.log('✅ playQuiz: isPublicMode 설정:', isPublicMode);
    } else {
      console.warn('⚠️ playQuiz: isPublicMode가 undefined입니다');
    }
    // meta.quizOrder는 더 이상 사용하지 않음

    await updateDoc(metaRef, metaUpdates);

    const basePath = `quizzes.${quizId}`;
    const quizUpdates: Record<string, any> = {
      [`${basePath}.answeredStudentsByGroup.${resolvedGroup}`]: [],
      [`${basePath}.updatedAt`]: serverTimestamp(),
    };
    // 그룹별 응답 필드는 없으면 생성 (기존 데이터는 유지)
    if (!quizData?.responsesByGroup || !quizData.responsesByGroup?.[resolvedGroup]) {
      quizUpdates[`${basePath}.responsesByGroup.${resolvedGroup}`] = {};
    }
    // A 그룹은 레거시 필드도 유지 (호환)
    if (resolvedGroup === 'A') {
      quizUpdates[`${basePath}.answeredStudents`] = [];
      if (!quizData.responses) {
        quizUpdates[`${basePath}.responses`] = {};
      }
    }

    await updateDoc(targetSetRef, quizUpdates);
  }

  async playQuizSet(
    classCode: string,
    setId: string,
    isMultiAnswerMode?: boolean,
    isPublicMode?: boolean,
    memberGroup?: string
  ): Promise<void> {
    const { metaRef, userId, classId } = await this.getClassChatRefs(classCode);
    const resolvedGroup = this.resolveMemberGroup(userId, classId, memberGroup);
    const setRef = doc(db, 'users', userId, 'classes', classId, 'ClassChatQuizSets', setId);
    const setSnap = await getDoc(setRef);
    if (!setSnap.exists()) {
      throw new Error('퀴즈 세트를 찾을 수 없습니다.');
    }
    const setData = setSnap.data() as QuizSetDoc;
    const quizEntries = Object.entries(setData.quizzes || {}).map(([id, q]) => ({
      ...(q as ChatQuiz),
      id,
    }));
    if (quizEntries.length === 0) {
      throw new Error('세트에 문항이 없습니다.');
    }
    const firstQuiz = quizEntries.sort((a, b) => (a.order ?? 0) - (b.order ?? 0))[0];
    if (!firstQuiz?.id) {
      throw new Error('퀴즈 문서를 찾을 수 없습니다.');
    }

    const metaUpdates: Record<string, any> = {
      activeQuizId: firstQuiz.id,
      activeQuizSetId: setId,
      activeQuizContainerId: setId,
      isChatOpen: true, // 세트 재생 시 챗을 자동으로 연다
      activeMemberGroup: resolvedGroup,
      lastUpdated: serverTimestamp(),
    };
    if (isMultiAnswerMode !== undefined) {
      metaUpdates.isMultiAnswerMode = isMultiAnswerMode;
    }
    if (isPublicMode !== undefined) {
      metaUpdates.isPublicMode = isPublicMode;
    }
    await updateDoc(metaRef, metaUpdates);

    const basePath = `quizzes.${firstQuiz.id}`;
    const quizUpdates: Record<string, any> = {
      [`${basePath}.answeredStudentsByGroup.${resolvedGroup}`]: [],
      [`${basePath}.updatedAt`]: serverTimestamp(),
    };
    if (!(firstQuiz as any)?.responsesByGroup || !(firstQuiz as any).responsesByGroup?.[resolvedGroup]) {
      quizUpdates[`${basePath}.responsesByGroup.${resolvedGroup}`] = {};
    }
    if (resolvedGroup === 'A') {
      quizUpdates[`${basePath}.answeredStudents`] = [];
      if (!firstQuiz.responses) {
        quizUpdates[`${basePath}.responses`] = {};
      }
    }
    // 해당 세트 문서를 업데이트 (main 세트가 아님)
    await updateDoc(setRef, quizUpdates);
  }

  async updateActiveQuizSettings(
    classCode: string,
    settings: { isMultiAnswerMode?: boolean; isPublicMode?: boolean }
  ): Promise<void> {
    if (settings.isMultiAnswerMode === undefined && settings.isPublicMode === undefined) {
      return;
    }

    const { metaRef, quizzesCollectionRef } = await this.getClassChatRefs(classCode);
    await this.ensureMetaDoc(classCode, metaRef, quizzesCollectionRef);
    await updateDoc(metaRef, {
      ...(settings.isMultiAnswerMode !== undefined && { isMultiAnswerMode: settings.isMultiAnswerMode }),
      ...(settings.isPublicMode !== undefined && { isPublicMode: settings.isPublicMode }),
      lastUpdated: serverTimestamp(),
    });
  }

  // 학생 문제 만들기 모드 토글 (메타 플래그)
  async setStudentCreateMode(
    classCode: string,
    enabled: boolean,
    setId?: string | null
  ): Promise<void> {
    const { metaRef, quizzesCollectionRef } = await this.getClassChatRefs(classCode);
    await this.ensureMetaDoc(classCode, metaRef, quizzesCollectionRef);
    await updateDoc(metaRef, {
      studentCreateMode: enabled,
      studentCreateSetId: enabled ? (setId ?? null) : null,
      lastUpdated: serverTimestamp(),
    });
  }

  async stopQuiz(classCode: string): Promise<void> {
    const { metaRef } = await this.getClassChatRefs(classCode);
    await updateDoc(metaRef, {
      activeQuizId: null,
      activeQuizSetId: null,
      activeQuizContainerId: null,
      activeMemberGroup: null,
      lastUpdated: serverTimestamp(),
    });
  }

  async getActiveQuiz(classCode: string): Promise<ChatQuiz | null> {
    const { metaRef, userId, classId } = await this.getClassChatRefs(classCode);
    const metaSnap = await getDoc(metaRef);
    if (!metaSnap.exists()) {
      return null;
    }
    const meta = metaSnap.data() as ClassChatMetaDoc;
    if (!meta.activeQuizId) {
      return null;
    }

    // activeQuizContainerId를 사용하여 해당 세트에서 찾기 (없으면 null 반환)
    const containerId = (meta as any).activeQuizContainerId ?? meta.activeQuizSetId;
    if (!containerId) {
      return null;
    }

    const targetSetRef = doc(db, 'users', userId, 'classes', classId, 'ClassChatQuizSets', containerId);
    const setSnap = await getDoc(targetSetRef);
    if (!setSnap.exists()) {
      return null;
    }
    const quizSet = setSnap.data() as QuizSetDoc;
    const quizData = quizSet.quizzes?.[meta.activeQuizId];
    if (!quizData) {
      return null;
    }

    return this.normalizeQuizData(meta.activeQuizId, quizData);
  }

  listenToActiveQuiz(
    classCode: string,
    callback: (
      quiz: ChatQuiz | null,
      activeQuizId: string | null,
      isMultiAnswerMode?: boolean,
      isPublicMode?: boolean,
      metaInfo?: {
        isChatOpen?: boolean;
        connectedStudents?: { [studentId: string]: ConnectedStudentInfo };
        activeQuizSetId?: string | null;
        activeQuizContainerId?: string | null;
        studentCreateMode?: boolean;
        studentCreateSetId?: string | null;
        activeMemberGroup?: string | null;
      }
    ) => void
    ,
    memberGroup?: string
  ): () => void {
    let metaUnsubscribe: (() => void) | null = null;
    let quizSetUnsubscribe: (() => void) | null = null;
    let shouldUnsubscribe = false;
    let latestMeta: ClassChatMetaDoc | null = null;
    let latestQuizSet: QuizSetDoc | null = null;
    let currentSetId: string | null = null;
    let userId: string | null = null;
    let classId: string | null = null;
    let currentSetLoaded = false;
    let desiredGroup: string | null = null;

    const teardown = () => {
      if (metaUnsubscribe) {
        metaUnsubscribe();
        metaUnsubscribe = null;
      }
      if (quizSetUnsubscribe) {
        quizSetUnsubscribe();
        quizSetUnsubscribe = null;
      }
    };

    const emit = () => {
      if (!latestMeta) {
        callback(null, null, false, false, {
          isChatOpen: false,
          connectedStudents: {},
          activeMemberGroup: null,
        });
        return;
      }

      const activeMemberGroupRaw = (latestMeta as any).activeMemberGroup;
      const resolvedActiveGroup =
        activeMemberGroupRaw !== undefined &&
        activeMemberGroupRaw !== null &&
        String(activeMemberGroupRaw).trim() !== ''
          ? String(activeMemberGroupRaw).trim().toUpperCase()
          : null;
      const group = (desiredGroup ?? 'A').toString().trim().toUpperCase();

      // 퀴즈/세트 재생 중일 때만 그룹 격리. stopQuiz 후 activeMemberGroup이 null이면
      // 예전 코드는 이를 'A'로 간주해 B그룹 코디 화면에서 isChatOpen이 꺼지는 버그가 있었음.
      const hasActiveQuizSession = !!(
        latestMeta.activeQuizId ||
        latestMeta.activeQuizSetId ||
        (latestMeta as any).activeQuizContainerId
      );

      if (hasActiveQuizSession && resolvedActiveGroup != null && resolvedActiveGroup !== group) {
        if (quizSetUnsubscribe) {
          quizSetUnsubscribe();
          quizSetUnsubscribe = null;
        }
        latestQuizSet = null;
        currentSetId = null;
        currentSetLoaded = false;

        callback(null, null, false, false, {
          isChatOpen: false,
          connectedStudents: {},
          activeQuizSetId: null,
          activeQuizContainerId: null,
          studentCreateMode: false,
          studentCreateSetId: null,
          activeMemberGroup: resolvedActiveGroup,
        });
        return;
      }

      const activeId = latestMeta.activeQuizId;
      // 데이터 컨테이너(퀴즈가 저장된 세트 문서)를 기준으로 구독한다.
      // - 단일 재생(세트에 속한 퀴즈 포함): activeQuizContainerId (playQuiz에서 설정됨)
      // - 구버전 호환: activeQuizSetId
      // activeQuizContainerId가 명시적으로 설정되어 있으면 우선 사용, 없으면 activeQuizSetId 사용
      // main을 기본값으로 사용하지 않음 (세트가 명시되어야 함)
      const activeContainerId =
        (latestMeta as any).activeQuizContainerId ?? latestMeta.activeQuizSetId;
      
      // activeContainerId가 없으면 퀴즈를 찾을 수 없음
      // 하지만 문제 만들기 모드일 때는 studentCreateMode 정보를 전달해야 함
      if (!activeContainerId) {
        const connectedByGroup =
          ((latestMeta as any).connectedStudentsByGroup &&
            (latestMeta as any).connectedStudentsByGroup[group]) ||
          (group === 'A' ? latestMeta.connectedStudents : {}) ||
          {};
        callback(null, null, false, false, {
          isChatOpen: latestMeta.isChatOpen ?? false,
          connectedStudents: connectedByGroup ?? {},
          activeQuizSetId: null,
          activeQuizContainerId: null,
          studentCreateMode: (latestMeta as any).studentCreateMode ?? false,
          studentCreateSetId: (latestMeta as any).studentCreateSetId ?? null,
          activeMemberGroup: resolvedActiveGroup,
        });
        return;
      }

      // 세트 전환 중이면 (아직 새 세트 스냅샷을 받지 못한 상태) 퀴즈 본문은 보류하되,
      // 접속자 맵·챗 오픈 등 메타는 즉시 전달해야 코디/학생 UI가 동기화된다.
      const isStudentCreateMode = (latestMeta as any).studentCreateMode ?? false;
      if ((activeContainerId !== currentSetId || !currentSetLoaded) && !isStudentCreateMode) {
        const connectedByGroupPending =
          ((latestMeta as any).connectedStudentsByGroup &&
            (latestMeta as any).connectedStudentsByGroup[group]) ||
          (group === 'A' ? latestMeta.connectedStudents : {}) ||
          {};
        callback(null, activeId ?? null, latestMeta.isMultiAnswerMode, latestMeta.isPublicMode, {
          isChatOpen: latestMeta.isChatOpen ?? false,
          connectedStudents: connectedByGroupPending ?? {},
          activeQuizSetId: latestMeta.activeQuizSetId ?? null,
          activeQuizContainerId: (latestMeta as any).activeQuizContainerId ?? null,
          studentCreateMode: (latestMeta as any).studentCreateMode ?? false,
          studentCreateSetId: (latestMeta as any).studentCreateSetId ?? null,
          activeMemberGroup: resolvedActiveGroup,
        });
        return;
      }
      
      // 문제 만들기 모드이고 세트가 로드되지 않았으면 studentCreateMode 정보만 전달
      if (isStudentCreateMode && (activeContainerId !== currentSetId || !currentSetLoaded)) {
        const connectedByGroup =
          ((latestMeta as any).connectedStudentsByGroup &&
            (latestMeta as any).connectedStudentsByGroup[group]) ||
          (group === 'A' ? latestMeta.connectedStudents : {}) ||
          {};
        callback(null, null, false, false, {
          isChatOpen: latestMeta.isChatOpen ?? false,
          connectedStudents: connectedByGroup ?? {},
          activeQuizSetId: latestMeta.activeQuizSetId ?? null,
          activeQuizContainerId: (latestMeta as any).activeQuizContainerId ?? null,
          studentCreateMode: true,
          studentCreateSetId: (latestMeta as any).studentCreateSetId ?? null,
          activeMemberGroup: resolvedActiveGroup,
        });
        return;
      }

      const quizData = activeId ? latestQuizSet?.quizzes?.[activeId] : null;
      if (activeId && !quizData) {
        console.warn(`⚠️ 퀴즈 데이터를 찾을 수 없습니다: quizId=${activeId}, containerId=${activeContainerId}, currentSetId=${currentSetId}, quizzes=${Object.keys(latestQuizSet?.quizzes || {}).join(', ')}`);
      }
      const normalized = activeId && quizData ? this.normalizeQuizData(activeId, quizData) : null;

      const connectedByGroup =
        ((latestMeta as any).connectedStudentsByGroup &&
          (latestMeta as any).connectedStudentsByGroup[group]) ||
        (group === 'A' ? latestMeta.connectedStudents : {}) ||
        {};

      callback(normalized, activeId, latestMeta.isMultiAnswerMode, latestMeta.isPublicMode, {
        isChatOpen: latestMeta.isChatOpen ?? false,
        connectedStudents: connectedByGroup ?? {},
        activeQuizSetId: latestMeta.activeQuizSetId ?? null,
        activeQuizContainerId: (latestMeta as any).activeQuizContainerId ?? null,
        studentCreateMode: (latestMeta as any).studentCreateMode ?? false,
        studentCreateSetId: (latestMeta as any).studentCreateSetId ?? null,
        activeMemberGroup: resolvedActiveGroup,
      });
    };

    const subscribeToSet = async (setId: string) => {
      if (!userId || !classId) return;

      const nextRef = doc(db, 'users', userId, 'classes', classId, 'ClassChatQuizSets', setId);

      // 이전 리스너 해제
      if (quizSetUnsubscribe) {
        quizSetUnsubscribe();
        quizSetUnsubscribe = null;
      }

      // 세트 문서가 없으면 자동 생성하지 않음 (onSnapshot이 빈 데이터를 반환)
      // 문서가 없으면 에러를 던지지 않고 빈 데이터로 처리
      currentSetLoaded = false;
      quizSetUnsubscribe = onSnapshot(nextRef, (setSnap) => {
        if (!setSnap.exists()) {
          console.warn(`⚠️ 세트 문서가 존재하지 않습니다: ${setId}`);
          latestQuizSet = { quizzes: {} };
          currentSetLoaded = true;
          emit(); // 문서가 없어도 emit하여 null 퀴즈를 콜백으로 전달
          return;
        }
        const data = setSnap.data() as QuizSetDoc;
        latestQuizSet = {
          quizzes: data.quizzes || {},
          lastUpdated: data.lastUpdated,
        };
        console.log(`✅ 세트 스냅샷 로드 완료: ${setId}, 퀴즈 개수: ${Object.keys(latestQuizSet.quizzes).length}`);
        currentSetLoaded = true;
        emit();
      }, (error) => {
        console.error(`❌ 세트 스냅샷 에러: ${setId}`, error);
        latestQuizSet = { quizzes: {} };
        currentSetLoaded = true;
        emit(); // 에러 발생 시에도 emit하여 null 퀴즈를 콜백으로 전달
      });
    };

    const setup = async () => {
      try {
        const { metaRef, quizzesCollectionRef, userId: uid, classId: cid } = await this.getClassChatRefs(classCode);
        userId = uid;
        classId = cid;
        // memberGroup이 undefined인 경우 localStorage 값을 사용하므로,
        // 그룹 전환(로컬 저장값 변경)도 반영될 수 있도록 이후에도 계속 갱신한다.
        desiredGroup = this.resolveMemberGroup(uid, cid, memberGroup);
        await this.ensureMetaDoc(classCode, metaRef, quizzesCollectionRef);

        if (shouldUnsubscribe) {
          return;
        }

        metaUnsubscribe = onSnapshot(metaRef, async (metaSnap) => {
          latestMeta = metaSnap.exists() ? (metaSnap.data() as ClassChatMetaDoc) : null;
          // 그룹 전환을 반영 (memberGroup이 undefined면 localStorage 기반으로 재계산)
          desiredGroup = this.resolveMemberGroup(uid, cid, memberGroup);

          // activeQuizContainerId가 명시적으로 설정되어 있으면 우선 사용, 없으면 activeQuizSetId 사용
          // main을 기본값으로 사용하지 않음 (세트가 명시되어야 함)
          const nextContainerId =
            (latestMeta as any)?.activeQuizContainerId ?? latestMeta?.activeQuizSetId;
          console.log(`🔍 listenToActiveQuiz: nextContainerId=${nextContainerId}, currentSetId=${currentSetId}, activeQuizId=${latestMeta?.activeQuizId}`);
          
          if (nextContainerId && nextContainerId !== currentSetId) {
            console.log(`🔄 세트 전환: ${currentSetId} -> ${nextContainerId}`);
            currentSetId = nextContainerId;
            await subscribeToSet(nextContainerId);
            return; // 세트 스냅샷을 받고 나서 emit
          } else if (!nextContainerId && currentSetId) {
            // 세트가 없어졌으면 구독 해제
            console.log(`🔄 세트 구독 해제: ${currentSetId}`);
            if (quizSetUnsubscribe) {
              quizSetUnsubscribe();
              quizSetUnsubscribe = null;
            }
            currentSetId = null;
            currentSetLoaded = false;
            latestQuizSet = { quizzes: {} };
            emit(); // null 퀴즈를 콜백으로 전달
            return;
          }

          emit();
        });

        // 초기 meta 스냅샷을 받아서 초기 세트 구독
        const initialMetaSnap = await getDoc(metaRef);
        latestMeta = initialMetaSnap.exists() ? (initialMetaSnap.data() as ClassChatMetaDoc) : null;
        const initialContainerId = (latestMeta as any)?.activeQuizContainerId ?? latestMeta?.activeQuizSetId;
        if (initialContainerId) {
          currentSetId = initialContainerId;
          await subscribeToSet(initialContainerId);
        }

        if (shouldUnsubscribe) {
          teardown();
        }
      } catch (error) {
        console.error('❌ 활성 퀴즈 리스너 등록 실패:', error);
        callback(null, null, false, false, {
          isChatOpen: false,
          connectedStudents: {},
        });
      }
    };

    void setup();

    return () => {
      shouldUnsubscribe = true;
      teardown();
    };
  }

  async submitResponse(
    classCode: string,
    quizId: string,
    studentId: string,
    answer: number[] | string,
    quiz: ChatQuiz,
    isMultiAnswerMode?: boolean,
    guestName?: string,
    memberGroup?: string
  ): Promise<void> {
    const { metaRef, userId, classId } = await this.getClassChatRefs(classCode);
    const resolvedGroup = this.resolveMemberGroup(userId, classId, memberGroup);
    
    // 메타에서 activeQuizContainerId를 확인하여 해당 세트에서 응답 저장
    const metaSnap = await getDoc(metaRef);
    if (!metaSnap.exists()) {
      throw new Error('퀴즈 메타 정보를 찾을 수 없습니다.');
    }
    const meta = metaSnap.data() as ClassChatMetaDoc;
    const rawActiveGroup = (meta as any).activeMemberGroup;
    const explicitActiveGroup =
      rawActiveGroup !== undefined &&
      rawActiveGroup !== null &&
      String(rawActiveGroup).trim() !== ''
        ? String(rawActiveGroup).trim().toUpperCase()
        : null;
    const hasActiveQuizSession = !!(
      meta.activeQuizId ||
      meta.activeQuizSetId ||
      (meta as any).activeQuizContainerId
    );
    if (hasActiveQuizSession && explicitActiveGroup != null && explicitActiveGroup !== resolvedGroup) {
      console.log('ℹ️ submitResponse 차단: 다른 그룹 재생 중', {
        explicitActiveGroup,
        resolvedGroup,
        studentId,
      });
      return;
    }
    const containerId = (meta as any).activeQuizContainerId ?? meta.activeQuizSetId;
    
    if (!containerId) {
      throw new Error('퀴즈가 속한 세트를 찾을 수 없습니다.');
    }

    const targetSetRef = doc(db, 'users', userId, 'classes', classId, 'ClassChatQuizSets', containerId);
    const setSnap = await getDoc(targetSetRef);
    if (!setSnap.exists()) {
      throw new Error(`퀴즈 세트를 찾을 수 없습니다: ${containerId}`);
    }
    const quizSet = setSnap.data() as QuizSetDoc;
    const quizData = quizSet.quizzes?.[quizId];

    if (!quizData) {
      throw new Error('퀴즈 문서를 찾을 수 없습니다.');
    }

    let isCorrect: boolean | undefined = undefined;
    if (quiz.type === 'choice' && Array.isArray(answer)) {
      const sortedAnswer = [...answer].sort();
      const sortedCorrect = [...(quiz.correctAnswers || [])].sort();
      isCorrect =
        sortedAnswer.length === sortedCorrect.length &&
        sortedAnswer.every((val, idx) => val === sortedCorrect[idx]);
    } else if (quiz.type === 'short' && typeof answer === 'string') {
      const normalizedAnswer = answer.trim().toLowerCase();
      isCorrect = (quiz.answers || []).some((a) => a.trim().toLowerCase() === normalizedAnswer);
    }

    const newResponse: StudentQuizResponse = {
      answer,
      submittedAt: Timestamp.now(),
      ...(guestName ? { guestName } : {}),
    };

    if (isCorrect !== undefined) {
      newResponse.isCorrect = isCorrect;
    }

    const { responses } = this.getQuizGroupData(quizData, resolvedGroup);

    const updatePayload: Record<string, any> = {
      [`quizzes.${quizId}.updatedAt`]: serverTimestamp(),
    };

    if (isMultiAnswerMode) {
      const existingResponse = responses[studentId];
      let responsesArray: StudentQuizResponse[] = [];

      if (Array.isArray(existingResponse)) {
        responsesArray = [...existingResponse];
      } else if (existingResponse) {
        responsesArray = [existingResponse];
      } else {
        responsesArray = [];
      }

      if (responsesArray.length >= MAX_MULTI_RESPONSES) {
        throw new Error('MAX_MULTI_RESPONSES_REACHED');
      }

      responsesArray = [...responsesArray, newResponse];

      updatePayload[`quizzes.${quizId}.responsesByGroup.${resolvedGroup}.${studentId}`] = responsesArray;
      if (resolvedGroup === 'A') {
        updatePayload[`quizzes.${quizId}.responses.${studentId}`] = responsesArray;
      }
    } else {
      updatePayload[`quizzes.${quizId}.responsesByGroup.${resolvedGroup}.${studentId}`] = newResponse;
      if (resolvedGroup === 'A') {
        updatePayload[`quizzes.${quizId}.responses.${studentId}`] = newResponse;
      }
    }

    // 첫 응답 여부는 arrayUnion으로 관리
    updatePayload[`quizzes.${quizId}.answeredStudentsByGroup.${resolvedGroup}`] = arrayUnion(studentId);
    if (resolvedGroup === 'A') {
      updatePayload[`quizzes.${quizId}.answeredStudents`] = arrayUnion(studentId);
    }

    // 해당 세트 문서에 업데이트 (main 세트가 아님)
    await updateDoc(targetSetRef, updatePayload);
  }

  async submitResponseToSet(
    classCode: string,
    setId: string,
    quizId: string,
    studentId: string,
    answer: number[] | string,
    quiz: ChatQuiz,
    isMultiAnswerMode?: boolean,
    guestName?: string,
    memberGroup?: string
  ): Promise<void> {
    const { userId, classId, metaRef } = await this.getClassChatRefs(classCode);
    const resolvedGroup = this.resolveMemberGroup(userId, classId, memberGroup);

    // 다른 그룹 재생 중이면 응답 저장을 막는다.
    const metaSnap = await getDoc(metaRef);
    if (metaSnap.exists()) {
      const meta = metaSnap.data() as ClassChatMetaDoc;
      const rawActiveGroup = (meta as any).activeMemberGroup;
      const explicitActiveGroup =
        rawActiveGroup !== undefined &&
        rawActiveGroup !== null &&
        String(rawActiveGroup).trim() !== ''
          ? String(rawActiveGroup).trim().toUpperCase()
          : null;
      const hasActiveQuizSession = !!(
        meta.activeQuizId ||
        meta.activeQuizSetId ||
        (meta as any).activeQuizContainerId
      );
      if (hasActiveQuizSession && explicitActiveGroup != null && explicitActiveGroup !== resolvedGroup) {
        console.log('ℹ️ submitResponseToSet 차단: 다른 그룹 재생 중', {
          explicitActiveGroup,
          resolvedGroup,
          studentId,
        });
        return;
      }
    }
    const setRef = doc(db, 'users', userId, 'classes', classId, 'ClassChatQuizSets', setId);
    const setSnap = await getDoc(setRef);
    if (!setSnap.exists()) {
      throw new Error('퀴즈 세트를 찾을 수 없습니다.');
    }
    const setData = setSnap.data() as QuizSetDoc;
    const quizData = setData.quizzes?.[quizId];
    if (!quizData) {
      throw new Error('퀴즈 문서를 찾을 수 없습니다.');
    }

    let isCorrect: boolean | undefined = undefined;
    if (quiz.type === 'choice' && Array.isArray(answer)) {
      const sortedAnswer = [...answer].sort();
      const sortedCorrect = [...(quiz.correctAnswers || [])].sort();
      isCorrect =
        sortedAnswer.length === sortedCorrect.length &&
        sortedAnswer.every((val, idx) => val === sortedCorrect[idx]);
    } else if (quiz.type === 'short' && typeof answer === 'string') {
      const normalizedAnswer = answer.trim().toLowerCase();
      isCorrect = (quiz.answers || []).some((a) => a.trim().toLowerCase() === normalizedAnswer);
    }

    const newResponse: StudentQuizResponse = {
      answer,
      submittedAt: Timestamp.now(),
      ...(guestName ? { guestName } : {}),
    };
    if (isCorrect !== undefined) {
      newResponse.isCorrect = isCorrect;
    }

    const { responses } = this.getQuizGroupData(quizData, resolvedGroup);

    const updatePayload: Record<string, any> = {
      [`quizzes.${quizId}.updatedAt`]: serverTimestamp(),
    };

    if (isMultiAnswerMode) {
      const existingResponse = responses[studentId];
      let responsesArray: StudentQuizResponse[] = [];

      if (Array.isArray(existingResponse)) {
        responsesArray = [...existingResponse];
      } else if (existingResponse) {
        responsesArray = [existingResponse];
      }

      if (responsesArray.length >= MAX_MULTI_RESPONSES) {
        throw new Error('MAX_MULTI_RESPONSES_REACHED');
      }

      responsesArray = [...responsesArray, newResponse];
      updatePayload[`quizzes.${quizId}.responsesByGroup.${resolvedGroup}.${studentId}`] = responsesArray;
      if (resolvedGroup === 'A') {
        updatePayload[`quizzes.${quizId}.responses.${studentId}`] = responsesArray;
      }
    } else {
      updatePayload[`quizzes.${quizId}.responsesByGroup.${resolvedGroup}.${studentId}`] = newResponse;
      if (resolvedGroup === 'A') {
        updatePayload[`quizzes.${quizId}.responses.${studentId}`] = newResponse;
      }
    }

    updatePayload[`quizzes.${quizId}.answeredStudentsByGroup.${resolvedGroup}`] = arrayUnion(studentId);
    if (resolvedGroup === 'A') {
      updatePayload[`quizzes.${quizId}.answeredStudents`] = arrayUnion(studentId);
    }

    await updateDoc(setRef, updatePayload);
  }

  async updateTypingStatus(
    classCode: string,
    quizId: string,
    studentId: string,
    isTyping: boolean,
    _isMultiAnswerMode?: boolean
  ): Promise<void> {
    // 레거시 API – 접속 여부만 관리하던 용도였으나,
    // 이제는 연결/응답 상태를 다른 필드로 관리하므로 noop 처리
    console.log('[ChatQuizService] updateTypingStatus는 더 이상 사용되지 않습니다.', {
      classCode,
      quizId,
      studentId,
      isTyping,
    });
    return Promise.resolve();
  }

  async addConnectedStudent(
    classCode: string,
    quizId: string,
    studentId: string
  ): Promise<void> {
    const { metaRef, userId, classId } = await this.getClassChatRefs(classCode);
    
    // 메타에서 activeQuizContainerId를 확인하여 해당 세트에 업데이트
    const metaSnap = await getDoc(metaRef);
    if (!metaSnap.exists()) {
      return; // 메타가 없으면 무시
    }
    const meta = metaSnap.data() as ClassChatMetaDoc;
    const containerId = (meta as any).activeQuizContainerId ?? meta.activeQuizSetId;
    
    if (!containerId) {
      return; // 세트가 없으면 무시
    }

    const targetSetRef = doc(db, 'users', userId, 'classes', classId, 'ClassChatQuizSets', containerId);
    await updateDoc(targetSetRef, {
      [`quizzes.${quizId}.connectedStudents`]: arrayUnion(studentId),
      [`quizzes.${quizId}.updatedAt`]: serverTimestamp(),
    }).catch(() => {
      // 퀴즈 세트나 퀴즈가 없으면 무시
    });
  }

  async removeConnectedStudent(
    classCode: string,
    quizId: string,
    studentId: string
  ): Promise<void> {
    const { metaRef, userId, classId } = await this.getClassChatRefs(classCode);
    
    // 메타에서 activeQuizContainerId를 확인하여 해당 세트에 업데이트
    const metaSnap = await getDoc(metaRef);
    if (!metaSnap.exists()) {
      return; // 메타가 없으면 무시
    }
    const meta = metaSnap.data() as ClassChatMetaDoc;
    const containerId = (meta as any).activeQuizContainerId ?? meta.activeQuizSetId;
    
    if (!containerId) {
      return; // 세트가 없으면 무시
    }

    const targetSetRef = doc(db, 'users', userId, 'classes', classId, 'ClassChatQuizSets', containerId);
    await updateDoc(targetSetRef, {
      [`quizzes.${quizId}.connectedStudents`]: arrayRemove(studentId),
      [`quizzes.${quizId}.updatedAt`]: serverTimestamp(),
    }).catch(() => {
      // 퀴즈 세트나 퀴즈가 없으면 무시
    });
  }

  async getQuizResponses(
    classCode: string,
    quizId: string,
    setId?: string, // 문제세트 모드일 때 직접 세트 ID 전달
    memberGroup?: string
  ): Promise<{
    responses: { [studentId: string]: StudentQuizResponse | StudentQuizResponse[] };
    answeredStudents?: string[];
  }> {
    const { metaRef, userId, classId } = await this.getClassChatRefs(classCode);
    const resolvedGroup = this.resolveMemberGroup(userId, classId, memberGroup);
    
    let containerId: string | null = null;
    
    // setId가 직접 제공되면 우선 사용 (문제세트 모드)
    if (setId) {
      containerId = setId;
    } else {
      // 메타에서 activeQuizContainerId를 확인하여 해당 세트에서 응답 가져오기
      const metaSnap = await getDoc(metaRef);
      if (!metaSnap.exists()) {
        return { responses: {}, answeredStudents: [] };
      }
      const meta = metaSnap.data() as ClassChatMetaDoc;
      containerId = (meta as any).activeQuizContainerId ?? meta.activeQuizSetId;
    }
    
    if (!containerId) {
      return { responses: {}, answeredStudents: [] };
    }

    const targetSetRef = doc(db, 'users', userId, 'classes', classId, 'ClassChatQuizSets', containerId);
    const setSnap = await getDoc(targetSetRef);
    if (!setSnap.exists()) {
      return { responses: {}, answeredStudents: [] };
    }
    const quizSet = setSnap.data() as QuizSetDoc;
    const data = quizSet.quizzes?.[quizId] as ChatQuiz | undefined;
    if (!data) {
      return { responses: {}, answeredStudents: [] };
    }
    const groupData = this.getQuizGroupData(data, resolvedGroup);
    return {
      responses: groupData.responses || {},
      answeredStudents: groupData.answeredStudents || [],
    };
  }

  async resetQuizResponses(classCode: string, quizId: string, memberGroup?: string): Promise<void> {
    const { metaRef, userId, classId } = await this.getClassChatRefs(classCode);
    const resolvedGroup = this.resolveMemberGroup(userId, classId, memberGroup);
    
    // 메타에서 activeQuizContainerId를 확인하여 해당 세트에서 응답 초기화
    const metaSnap = await getDoc(metaRef);
    if (!metaSnap.exists()) {
      return; // 메타가 없으면 무시
    }
    const meta = metaSnap.data() as ClassChatMetaDoc;
    const containerId = (meta as any).activeQuizContainerId ?? meta.activeQuizSetId;
    
    if (!containerId) {
      return; // 세트가 없으면 무시
    }

    const targetSetRef = doc(db, 'users', userId, 'classes', classId, 'ClassChatQuizSets', containerId);
    const updates: Record<string, any> = {
      [`quizzes.${quizId}.responsesByGroup.${resolvedGroup}`]: {},
      [`quizzes.${quizId}.answeredStudentsByGroup.${resolvedGroup}`]: [],
      [`quizzes.${quizId}.updatedAt`]: serverTimestamp(),
    };
    if (resolvedGroup === 'A') {
      updates[`quizzes.${quizId}.responses`] = {};
      updates[`quizzes.${quizId}.answeredStudents`] = [];
    }
    await updateDoc(targetSetRef, updates);
  }

  async resetStudentResponse(
    classCode: string,
    quizId: string,
    studentId: string,
    responseIndex?: number,
    memberGroup?: string
  ): Promise<void> {
    const { metaRef, userId, classId } = await this.getClassChatRefs(classCode);
    const resolvedGroup = this.resolveMemberGroup(userId, classId, memberGroup);
    
    // 메타에서 activeQuizContainerId를 확인하여 해당 세트에서 응답 초기화
    const metaSnap = await getDoc(metaRef);
    if (!metaSnap.exists()) {
      return; // 메타가 없으면 무시
    }
    const meta = metaSnap.data() as ClassChatMetaDoc;
    const containerId = (meta as any).activeQuizContainerId ?? meta.activeQuizSetId;
    
    if (!containerId) {
      return; // 세트가 없으면 무시
    }

    const targetSetRef = doc(db, 'users', userId, 'classes', classId, 'ClassChatQuizSets', containerId);
    const setSnap = await getDoc(targetSetRef);
    if (!setSnap.exists()) {
      return;
    }
    const quizSet = setSnap.data() as QuizSetDoc;
    const data = quizSet.quizzes?.[quizId] as ChatQuiz | undefined;
    if (!data) {
      return;
    }
    const { responses } = this.getQuizGroupData(data, resolvedGroup);

    const studentResponses = responses[studentId];

    if (!studentResponses) {
      return;
    }

    if (responseIndex === undefined || !Array.isArray(studentResponses)) {
      const updates: Record<string, any> = {
        [`quizzes.${quizId}.responsesByGroup.${resolvedGroup}.${studentId}`]: deleteField(),
        [`quizzes.${quizId}.updatedAt`]: serverTimestamp(),
      };
      if (resolvedGroup === 'A') {
        updates[`quizzes.${quizId}.responses.${studentId}`] = deleteField();
      }
      await updateDoc(targetSetRef, updates);
      return;
    }

    const targetResponses = [...studentResponses];

    if (responseIndex < 0 || responseIndex >= targetResponses.length) {
      return;
    }

    targetResponses.splice(responseIndex, 1);

    const updates: Record<string, any> = {
      [`quizzes.${quizId}.updatedAt`]: serverTimestamp(),
    };

    if (targetResponses.length > 0) {
      updates[`quizzes.${quizId}.responsesByGroup.${resolvedGroup}.${studentId}`] = targetResponses;
      if (resolvedGroup === 'A') {
        updates[`quizzes.${quizId}.responses.${studentId}`] = targetResponses;
      }
    } else {
      updates[`quizzes.${quizId}.responsesByGroup.${resolvedGroup}.${studentId}`] = deleteField();
      if (resolvedGroup === 'A') {
        updates[`quizzes.${quizId}.responses.${studentId}`] = deleteField();
      }
    }

    await updateDoc(targetSetRef, updates);
  }

  listenToResponses(
    classCode: string,
    quizId: string,
    callback: (
      responses: { [studentId: string]: StudentQuizResponse | StudentQuizResponse[] },
      answeredStudents?: string[]
    ) => void,
    setId?: string, // 문제세트 모드일 때 직접 세트 ID 전달
    memberGroup?: string
  ): () => void {
    let metaUnsubscribe: (() => void) | null = null;
    let quizSetUnsubscribe: (() => void) | null = null;
    let shouldUnsubscribe = false;
    let currentSetId: string | null = null;
    let userId: string | null = null;
    let classId: string | null = null;

    const teardown = () => {
      if (metaUnsubscribe) {
        metaUnsubscribe();
        metaUnsubscribe = null;
      }
      if (quizSetUnsubscribe) {
        quizSetUnsubscribe();
        quizSetUnsubscribe = null;
      }
    };

    const subscribeToSet = (setId: string) => {
      if (!userId || !classId) {
        console.warn('⚠️ subscribeToSet: userId 또는 classId가 없습니다.', { userId, classId });
        return;
      }

      if (quizSetUnsubscribe) {
        quizSetUnsubscribe();
        quizSetUnsubscribe = null;
      }

      const targetSetRef = doc(db, 'users', userId, 'classes', classId, 'ClassChatQuizSets', setId);
      console.log(`📡 응답 리스너 구독 시작: ${setId}, quizId: ${quizId}`);

      quizSetUnsubscribe = onSnapshot(targetSetRef, (snapshot) => {
          if (snapshot.exists()) {
            const data = snapshot.data() as QuizSetDoc;
            const quiz = data.quizzes?.[quizId] as ChatQuiz | undefined;
            if (quiz) {
              const resolvedGroup = this.resolveMemberGroup(userId!, classId!, memberGroup);
              const { responses, answeredStudents } = this.getQuizGroupData(quiz, resolvedGroup);
              const responseCount = Object.keys(responses).length;
              console.log(`✅ 응답 데이터 수신: ${setId}, quizId: ${quizId}, 응답 수: ${responseCount}`, responses);
              callback(
                responses,
                answeredStudents
              );
            } else {
              console.log(`⚠️ 퀴즈를 찾을 수 없음: ${setId}, quizId: ${quizId}`);
              callback({}, []);
            }
          } else {
            console.log(`⚠️ 세트 문서가 존재하지 않음: ${setId}`);
            callback({}, []);
          }
      }, (error) => {
        console.error(`❌ 응답 리스너 에러: ${setId}`, error);
        callback({}, []);
      });
    };

    const setup = async () => {
      try {
        const { metaRef, userId: uid, classId: cid } = await this.getClassChatRefs(classCode);
        userId = uid;
        classId = cid;

        if (shouldUnsubscribe) {
          return;
        }

        // setId가 직접 제공되면 바로 해당 세트 구독
        if (setId) {
          console.log(`🎯 직접 setId 제공됨: ${setId}, quizId: ${quizId}`);
          currentSetId = setId;
          subscribeToSet(setId);
          return;
        }

        // setId가 없으면 메타 리스너를 통해 activeQuizContainerId 찾기
        // 메타 리스너: activeQuizContainerId 변경 시 세트 전환
        metaUnsubscribe = onSnapshot(metaRef, (metaSnap) => {
          if (!metaSnap.exists()) {
            if (quizSetUnsubscribe) {
              quizSetUnsubscribe();
              quizSetUnsubscribe = null;
            }
            currentSetId = null;
            callback({}, []);
            return;
          }

          const meta = metaSnap.data() as ClassChatMetaDoc;
          const desired = this.resolveMemberGroup(uid, cid, memberGroup);
          const activeGroupRaw = (meta as any).activeMemberGroup ?? null;
          const activeGroup = (activeGroupRaw ?? 'A').toString().trim().toUpperCase();
          if (activeGroup && activeGroup !== desired) {
            // 다른 그룹이 활성 상태면, 이 그룹은 응답 리스너를 끊고 비워둔다.
            if (quizSetUnsubscribe) {
              quizSetUnsubscribe();
              quizSetUnsubscribe = null;
            }
            currentSetId = null;
            callback({}, []);
            return;
          }
          const nextContainerId = (meta as any).activeQuizContainerId ?? meta.activeQuizSetId;

          if (nextContainerId && nextContainerId !== currentSetId) {
            currentSetId = nextContainerId;
            subscribeToSet(nextContainerId);
          } else if (!nextContainerId && currentSetId) {
            // 세트가 없어졌으면 구독 해제
            if (quizSetUnsubscribe) {
              quizSetUnsubscribe();
              quizSetUnsubscribe = null;
            }
            currentSetId = null;
            callback({}, []);
          }
        });

        // 초기 meta 스냅샷을 받아서 초기 세트 구독
        const initialMetaSnap = await getDoc(metaRef);
        if (initialMetaSnap.exists()) {
          const meta = initialMetaSnap.data() as ClassChatMetaDoc;
          const initialContainerId = (meta as any).activeQuizContainerId ?? meta.activeQuizSetId;
          if (initialContainerId) {
            currentSetId = initialContainerId;
            subscribeToSet(initialContainerId);
          }
        }

        if (shouldUnsubscribe) {
          teardown();
        }
      } catch (error) {
        console.error('❌ 응답 리스너 등록 실패:', error);
        callback({}, []);
      }
    };

    void setup();

    return () => {
      shouldUnsubscribe = true;
      teardown();
    };
  }
}

export const chatQuizService = new ChatQuizService();

